import { Box, Flex } from "@chakra-ui/react";
import { SortDirection } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { Search, Table } from "components";
import { useCallback, useMemo, useState } from "react";
import { quantumDemandColumns } from "./QuantumDemand.columns";
import { listPaginatedQuantumDemand } from "./QuantumDemand.requests";
import {
  QuantumDemandDomainsFile,
  QuantumDemandFetchDataParams,
  QuantumDemandRow,
} from "./QuantumDemand.types";
import { QuantumDemandDomainsUpload } from "./QuantumDemandDomainsUpload";
import {
  BuyingGroupProfileModal,
  useBuyingGroupProfileParam,
} from "./components/BuyingGroupProfile";

interface QuantumDemandProps {
  campaignId: number;
}

const MAX_UPLOADED_DOMAINS = 1000;

const defaultListPaginatedCampaignsParams: QuantumDemandFetchDataParams = {
  page: 1,
  per_page: 10,
  order_by: "quantumIndex",
  order: SortDirection.DESC,
};

export function QuantumDemand({ campaignId }: QuantumDemandProps) {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [domainsFile, setDomainsFile] = useState<
    QuantumDemandDomainsFile | undefined
  >(undefined);

  const [fetchDataParams, setFetchDataParams] =
    useState<QuantumDemandFetchDataParams>(defaultListPaginatedCampaignsParams);

  const columns = useMemo<ColumnDef<QuantumDemandRow>[]>(
    () => quantumDemandColumns,
    [],
  );

  const queryParams: QuantumDemandFetchDataParams = domainsFile
    ? {
        ...defaultListPaginatedCampaignsParams,
        per_page: MAX_UPLOADED_DOMAINS,
        domains: domainsFile.domains,
      }
    : fetchDataParams;

  const { data, isFetching } = useQuery(
    ["activeQuantumDemandList", queryParams, campaignId],
    () => {
      return listPaginatedQuantumDemand(Number(campaignId), queryParams);
    },
  );

  const { domain: buyingGroupProfileDomain } = useBuyingGroupProfileParam();

  const handleOnFetchDataChange = useCallback(
    (params: QuantumDemandFetchDataParams) => {
      setFetchDataParams({
        ...params,
      });
    },
    [setFetchDataParams],
  );

  return (
    <Box px={4} pb={4} width="full">
      <Flex alignItems="center" gap={4}>
        <Search
          currentValue={search}
          placeholder="Search by domain"
          onSearch={setSearch}
        />

        <QuantumDemandDomainsUpload
          domainsFile={domainsFile}
          onDomainsFileChange={setDomainsFile}
        />
      </Flex>

      <Table<QuantumDemandRow>
        stretch
        maxWidth="100%"
        columns={columns}
        data={data?.results ?? []}
        meta={data?.meta}
        isFetching={isFetching}
        searchValue={search}
        isControlledByAPI={!domainsFile}
        defaultSort={[
          {
            id: defaultListPaginatedCampaignsParams.order_by,
            desc: true,
          },
        ]}
        total={domainsFile ? data?.results.length : undefined}
        onFetchDataChange={handleOnFetchDataChange}
        resetPaginationDependencies={[
          campaignId,
          search,
          domainsFile?.fileName,
        ]}
        isScrollable={false}
      />

      <BuyingGroupProfileModal
        campaignId={campaignId}
        domain={buyingGroupProfileDomain}
      />
    </Box>
  );
}
